import { ActionIcon, Alert, Box, Button, createStyles, Flex, Icon, Modal, Stack, TFText, Tooltip } from '@tf/ui';
import { fmt } from '@tf/utils';
import type React from 'react';
import { useState } from 'react';

import type { FileInfo } from '../../../../api/files';

import { DownloadLink } from './DownloadLink';
import { PreviewButton } from './PreviewButton';
import { isPreviewableFile } from './utils';

interface Props {
	file: FileInfo;
	onDelete: (file: FileInfo) => void;
	isReadOnly?: boolean;
}

const useStyles = createStyles(({ colors, radius }) => ({
	wrapper: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '.25rem .75rem',
		borderRadius: radius.sm,
		backgroundColor: colors.gray[1],
	},
	content: {
		flex: 1,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginRight: '.5rem',
	},
}));

export const DocumentItem: React.FC<Props> = ({ file, isReadOnly, onDelete }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { classes } = useStyles();

	const deleteHandler = () => {
		setIsOpen(false);
		onDelete(file);
	};

	return (
		<>
			<Box className={classes.wrapper}>
				<Box className={classes.content}>
					<DownloadLink file={file} />
					<Flex>
						{file.meta['@createdAt'] && (
							<TFText size="xs" c="dimmed">
								{fmt.toDate(file.meta['@createdAt'], { preset: 'full_date_with_time' })}
							</TFText>
						)}
						{file.meta['@size'] && (
							<TFText ml="8px" size="xs" c="dimmed">
								{fmt.toFileSize(file.meta['@size'])}
							</TFText>
						)}
					</Flex>
				</Box>

				<Flex gap={8}>
					{isPreviewableFile(file) && <PreviewButton file={file} />}
					<Tooltip label="Delete document" withArrow>
						<ActionIcon variant="subtle" color="red" onClick={() => setIsOpen(true)} hidden={isReadOnly}>
							<Icon.IconTrash size={18} stroke={1.5} />
						</ActionIcon>
					</Tooltip>
				</Flex>
			</Box>

			<Modal centered opened={isOpen} onClose={() => setIsOpen(false)} title="Delete confirmation">
				<Stack gap={9} p=".5rem 1rem">
					<TFText size="sm">Are you sure you want to delete this document?</TFText>
					<Alert color="gray" p=".25rem 1rem">
						<TFText fw={500}>{file.meta['@filename']}</TFText>
					</Alert>
					<Stack gap={9} mt="1rem">
						<Button
							color="red"
							leftSection={<Icon.IconTrash size={18} stroke={1.8} />}
							fullWidth
							onClick={deleteHandler}
						>
							Delete Document
						</Button>
						<Button fullWidth variant="light" onClick={() => setIsOpen(false)}>
							Cancel
						</Button>
					</Stack>
				</Stack>
			</Modal>
		</>
	);
};
